import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth';

/** Requests where a 401 means "bad credentials", not "expired access token" —
 *  never worth retrying. `/auth/logout` is @Public() (only ever acts on the
 *  refresh cookie) so a 401 there would never mean "expired access token"
 *  either. */
const NO_RETRY_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/register',
  '/auth/logout',
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const isApiRequest = req.url.startsWith(environment.apiBaseUrl);
  const token = auth.getAccessToken();

  const authedReq =
    token && isApiRequest
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(authedReq).pipe(
    catchError((err: unknown) => {
      const canRetry =
        isApiRequest &&
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        !NO_RETRY_PATHS.some((path) => req.url.includes(path));

      if (!canRetry) {
        return throwError(() => err);
      }

      return from(auth.refresh()).pipe(
        switchMap((newToken) =>
          next(
            req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }),
          ),
        ),
        catchError((refreshErr: unknown) => {
          auth.clearSession();
          // The refresh cookie is also gone/invalid — this session is
          // truly dead, not just this one request. Redirect to login
          // (preserving where the user was headed via returnUrl) instead
          // of leaving whatever component made this request to show a
          // generic "could not load" message with no path forward.
          void router.navigateByUrl(
            router.createUrlTree(['/login'], {
              queryParams: { returnUrl: router.url },
            }),
          );
          return throwError(() => refreshErr);
        }),
      );
    }),
  );
};
